const ICE = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

/**
 * Voice-only WebRTC call with a ready-handshake over the persistent
 * signaling channel. Both sides ping "ready" every 2s until the
 * offer/answer handshake completes, so no offer is ever lost.
 *
 * isCaller=true  → sends the offer when the callee is ready
 * isCaller=false → waits for the offer, answers
 */

function createPeerConnection({ send, isCaller, onRemoteStream, onStatus }) {
  const pc = new RTCPeerConnection(ICE);
  pc.onicecandidate = (e) => {
    if (e.candidate) send({ type: "ice", candidate: e.candidate });
  };
  pc.ontrack = (e) => {
    const s = e.streams && e.streams[0];
    if (s) onRemoteStream?.(s);
  };
  pc.onconnectionstatechange = () => onStatus?.(pc.connectionState);
  pc.oniceconnectionstatechange = () => {
    if (pc.iceConnectionState === "failed" && isCaller) {
      try { pc.restartIce(); } catch (e) { console.warn("[rtc] ice restart failed", e); }
    }
  };
  return pc;
}

async function attachMicrophone(pc, onStatus) {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));
    return stream;
  } catch (e) {
    console.warn("[rtc] microphone unavailable", e);
    onStatus?.("mic-denied");
    // Still carry an audio m-line so the peer's audio reaches us.
    try { pc.addTransceiver("audio", { direction: "recvonly" }); }
    catch (err) { console.warn("[rtc] transceiver fallback failed", err); }
    return null;
  }
}

function createMessageHandler({ pc, isCaller, send, onHandshakeDone, closeRef }) {
  const makeOffer = async () => {
    try {
      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);
      send({ type: "offer", sdp: offer });
    } catch (e) { console.warn("[rtc] offer failed", e); }
  };
  return async (msg) => {
    try {
      if (msg.type === "ready" && isCaller) {
        if (!pc.remoteDescription) await makeOffer();
      } else if (msg.type === "offer") {
        onHandshakeDone();
        await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        send({ type: "answer", sdp: answer });
      } else if (msg.type === "answer") {
        onHandshakeDone();
        await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
      } else if (msg.type === "ice" && msg.candidate) {
        await pc.addIceCandidate(new RTCIceCandidate(msg.candidate));
      } else if (msg.type === "bye") {
        closeRef.current?.(false);
      }
    } catch (e) {
      console.warn("[rtc] signaling error", msg.type, e);
    }
  };
}

function startReadyPing({ send, isHandshakeDone, isClosed }) {
  send({ type: "ready" });
  const timer = setInterval(() => {
    if (isClosed() || isHandshakeDone()) {
      clearInterval(timer);
      return;
    }
    send({ type: "ready" });
  }, 2000);
  return timer;
}

export function startVoiceCall({ signaling, peerId, isCaller, onRemoteStream, onStatus }) {
  const state = { closed: false };
  const timers = { ready: null, timeout: null };
  const closeRef = { current: null };
  let localStream = null;

  const send = (obj) => signaling.send({ ...obj, to: peerId });
  const pc = createPeerConnection({ send, isCaller, onRemoteStream, onStatus });

  const isHandshakeDone = () => (isCaller ? !!pc.remoteDescription : !!pc.remoteDescription);

  const onHandshakeDone = () => {
    if (timers.ready) { clearInterval(timers.ready); timers.ready = null; }
    if (timers.timeout) { clearTimeout(timers.timeout); timers.timeout = null; }
  };

  const handler = createMessageHandler({ pc, isCaller, send, onHandshakeDone, closeRef });
  const off = signaling.onMessage((msg) => {
    if (state.closed || msg.from !== peerId) return;
    handler(msg);
  });

  function close(sendBye = true) {
    if (state.closed) return;
    state.closed = true;
    if (timers.ready) { clearInterval(timers.ready); timers.ready = null; }
    if (timers.timeout) { clearTimeout(timers.timeout); timers.timeout = null; }
    off();
    if (sendBye) {
      try { send({ type: "bye" }); } catch (e) { console.warn("[rtc] bye failed", e); }
    }
    try { localStream?.getTracks().forEach((t) => t.stop()); } catch (e) { console.warn("[rtc] track stop failed", e); }
    try { pc.close(); } catch (e) { console.warn("[rtc] pc close failed", e); }
    onStatus?.("closed");
  }
  closeRef.current = close;

  (async () => {
    localStream = await attachMicrophone(pc, onStatus);
    timers.ready = startReadyPing({ send, isHandshakeDone, isClosed: () => state.closed });
    // Surface a visible warning if the handshake never completes
    timers.timeout = setTimeout(() => {
      timers.timeout = null;
      if (!state.closed && !isHandshakeDone()) onStatus?.("connection-timeout");
    }, 12000);
  })();

  return {
    close,
    setMuted(m) {
      try { localStream?.getAudioTracks().forEach((t) => (t.enabled = !m)); }
      catch (e) { console.warn("[rtc] mute failed", e); }
    },
  };
}
